/**
 * Withdrawal Bonus Warning Modal
 * 
 * Displays warning modal when user has active bonuses before withdrawal.
 * Warns that withdrawal will cancel active bonuses.
 * 
 * @package betbeast
 */

import { getCasinoRedirectUrl } from '../account-redirect-utils.js';

export class WithdrawalBonusWarningModal {
    constructor() {
        this.modal = null;
        this.proceedCallback = null;
        this.cancelCallback = null;
        this.initialized = false;
    }
    
    /**
     * Initialize the bonus warning modal
     */
    init() {
        this.modal = document.getElementById('wallet-withdrawal-bonus-warning-modal');
        
        if (!this.modal) {
            console.log('Bonus warning modal not found - user likely has no active bonuses');
            return false;
        }
        
        this.setupEventListeners();
        this.initialized = true;
        return true;
    }
    
    /**
     * Setup event listeners
     */
    setupEventListeners() {
        // Overlay click (close button)
        const overlay = this.modal.querySelector('.withdrawal-bonus-warning__overlay');
        if (overlay) {
            overlay.addEventListener('click', () => this.handleCancel());
        }
        
        // Keep Playing button (redirect to casino)
        const cancelBtn = this.modal.querySelector('.btn-cancel-withdrawal');
        if (cancelBtn) {
            cancelBtn.addEventListener('click', () => this.handleKeepPlaying());
        }
        
        // Proceed button
        const proceedBtn = this.modal.querySelector('.btn-proceed-withdrawal');
        if (proceedBtn) {
            proceedBtn.addEventListener('click', () => this.handleProceed());
        }
    }
    
    /**
     * Open the bonus warning modal
     * @param {Function} onProceed - Callback when user proceeds with withdrawal
     * @param {Function} onCancel - Callback when user cancels (optional)
     */
    open(onProceed, onCancel = null) {
        if (!this.modal) {
            console.warn('Cannot open bonus warning modal - not initialized');
            return;
        }
        
        this.proceedCallback = onProceed;
        this.cancelCallback = onCancel;
        
        this.modal.classList.add('is-open');
        this.modal.setAttribute('aria-hidden', 'false');
        document.body.classList.add('modal-open');
    }
    
    /**
     * Close the bonus warning modal
     */
    close() {
        if (this.modal) {
            this.modal.classList.remove('is-open');
            this.modal.setAttribute('aria-hidden', 'true');
            document.body.classList.remove('modal-open');
        }
        
        this.proceedCallback = null;
        this.cancelCallback = null;
    }
    
    /**
     * Handle proceed action
     */
    handleProceed() {
        console.log('Proceed clicked, callback exists:', !!this.proceedCallback);
        
        const callback = this.proceedCallback;
        this.close();
        
        if (callback) {
            callback();
        }
    }
    
    /**
     * Handle cancel action (overlay/ESC)
     */
    handleCancel() {
        this.close();
        
        if (this.cancelCallback) {
            this.cancelCallback();
        }
    }
    
    /**
     * Handle keep playing action (redirect to casino)
     */
    handleKeepPlaying() {
        this.close();
        // Redirect to casino lobby
        window.location.href = getCasinoRedirectUrl();
    }
    
    /**
     * Check if modal is initialized
     */
    isInitialized() {
        return this.initialized;
    }
    
    /**
     * Check if modal exists (user has active bonuses)
     */
    exists() {
        return this.modal !== null;
    }
}
